import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../utils/supabaseClient';
import truckImage from '../assets/amesTruck.png';
import acLogo from '../assets/aclogo.png';

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password
      });

      if (signInError) throw signInError;

      navigate('/home');
      
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setResetEmailSent(true);
      
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {resetEmailSent ? (
            <div className="bg-white p-8 rounded-2xl shadow-2xl text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Check Your Email</h3>
              <p className="text-gray-600 mb-6">
                We've sent a password reset link to <strong>{email}</strong>.
                Check your email and follow the instructions to reset your password.
              </p>
              
              <button
                onClick={() => {
                  setResetEmailSent(false);
                  setIsResetMode(false);
                }}
                className="w-full bg-red-600 text-white py-3 px-4 rounded-sm hover:bg-red-700 font-medium"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <div>
              <div className="text-left mb-6">
                <img className="h-12 w-auto" src={acLogo} alt="Ames Connect" />
                <h3 className="mt-6 text-3xl font-extrabold text-gray-900">
                  {isResetMode ? 'Reset Password' : 'Sign In'}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {isResetMode 
                    ? 'Enter your email to receive reset instructions'
                    : <>
                        Welcome back! Don't have an account?{' '}
                        <Link to="/signup" className="font-medium text-red-600 hover:text-red-500">
                          Sign up
                        </Link>
                      </>
                  }
                </p>
              </div>

              <form className="space-y-6" onSubmit={isResetMode ? handlePasswordReset : handleSignIn}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    disabled={loading}
                    required
                  />
                </div>

                {!isResetMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10"
                        disabled={loading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                    <div className="flex">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setIsResetMode(!isResetMode);
                        setError('');
                      }}
                      className="font-medium text-red-600 hover:text-red-500"
                    >
                      {isResetMode ? 'Back to sign in' : 'Forgot your password?'}
                    </button>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading || !email.trim() || (!isResetMode && !password.trim())}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-sm shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {loading 
                      ? (isResetMode ? 'Sending Reset Email...' : 'Signing In...') 
                      : (isResetMode ? 'Send Reset Email' : 'Sign In')
                    }
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      <div className="hidden lg:flex w-0 flex-1 bg-white border-l border-gray-200 items-center justify-center p-12">
        <div className="w-full max-w-md text-center">
          <img
            className="mx-auto h-auto w-full max-w-sm"
            src={truckImage}
            alt="Ames Profiler Truck"
          />
          <h2 className="mt-8 text-3xl font-extrabold text-gray-900">Pavement Analysis, Perfected.</h2>
          <p className="mt-4 text-lg text-gray-600">
            Leverage data to make smarter decisions about your road infrastructure projects.
          </p>
        </div>
      </div>
    </div>
  );
}